import React from 'react';
import { useBreakpoint } from '@shared/hooks/use-breakpoint';

export interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
  width?: string;
}

export interface ResponsiveDataListProps<T> {
  data: T[];
  keyExtractor: (item: T) => string;
  columns: Column<T>[];
  emptyMessage?: string;
  onItemClick?: (item: T) => void;
}

// ResponsiveDataList renders desktop column grid and mobile stacked touch-cards (M-I3, M-I7).
export function ResponsiveDataList<T>({
  data,
  keyExtractor,
  columns,
  emptyMessage = 'No records found',
  onItemClick,
}: ResponsiveDataListProps<T>): React.JSX.Element {
  const { isDesktop } = useBreakpoint();

  if (data.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
        {emptyMessage}
      </div>
    );
  }

  if (!isDesktop) {
    return (
      <div className="flex flex-col gap-3">
        {data.map((item) => (
          <div
            key={keyExtractor(item)}
            onClick={() => onItemClick?.(item)}
            className="p-4 rounded-lg border border-border bg-card text-card-foreground shadow-sm flex flex-col gap-2 min-h-[44px]"
          >
            {columns.map((col) => (
              <div key={col.key} className="flex justify-between items-center text-sm py-0.5">
                <span className="font-medium text-muted-foreground">{col.header}</span>
                <span className="text-foreground">{col.render(item)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full border border-border rounded-lg overflow-hidden bg-card text-card-foreground">
      <div className="flex border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3">
        {columns.map((col) => (
          <div key={col.key} className="flex-1" style={col.width ? { width: col.width, flex: 'none' } : undefined}>
            {col.header}
          </div>
        ))}
      </div>
      <div className="divide-y divide-border">
        {data.map((item) => (
          <div
            key={keyExtractor(item)}
            onClick={() => onItemClick?.(item)}
            className="flex items-center px-4 py-3 text-sm hover:bg-muted/20 transition-colors cursor-pointer"
          >
            {columns.map((col) => (
              <div key={col.key} className="flex-1" style={col.width ? { width: col.width, flex: 'none' } : undefined}>
                {col.render(item)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
